import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { SideNav } from './SideNav';
import { userService } from '../services/userService';
import { 
  PageLayout, 
  Container, 
  Card, 
  H2, 
  Body1, 
  Body2
} from './ui';

function Profile() {
  const { user, sideNavLocked, setSideNavLocked } = useAuth();
  
  // Force cache refresh
  
  // State for editing - now per section
  const [editingSections, setEditingSections] = useState({
    personal: false,
    jobSearch: false,
    skills: false,
    links: false
  });
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    employmentStatus: '',
    jobSearchStatus: '',
    interestedRoleKeywords: [],
    currentSkills: [],
    softwareFamiliarity: [],
    linkedinUrl: '',
    githubUrl: '',
    personalWebsiteUrl: ''
  });
  const [loading, setLoading] = useState({
    personal: false,
    jobSearch: false,
    skills: false,
    links: false
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form inputs for adding new items
  const [newSkill, setNewSkill] = useState('');
  const [newSoftware, setNewSoftware] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        employmentStatus: user.employmentStatus || '',
        jobSearchStatus: user.jobSearchStatus || '',
        interestedRoleKeywords: user.interestedRoleKeywords || [],
        currentSkills: user.currentSkills || [],
        softwareFamiliarity: user.softwareFamiliarity || [],
        linkedinUrl: user.linkedinUrl || '',
        githubUrl: user.githubUrl || '',
        personalWebsiteUrl: user.personalWebsiteUrl || ''
      });
    }
  }, [user]);

  // Get user's first name
  const getUserFirstName = () => {
    if (user?.firstName) {
      return user.firstName;
    }
    // Fallback to parsing name field if it exists (for backwards compatibility)
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    return 'User';
  };

  // Get user's last name  
  const getUserLastName = () => {
    if (user?.lastName) {
      return user.lastName;
    }
    // Fallback to parsing name field if it exists (for backwards compatibility)
    if (user?.name) {
      const nameParts = user.name.split(' ');
      return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
    }
    return '';
  };

  // Get user's first initial for profile picture
  const getUserInitial = () => {
    const firstName = getUserFirstName();
    return firstName.charAt(0).toUpperCase();
  };

  // Handle section save
  const handleSectionSave = async (section) => {
    try {
      setLoading(prev => ({ ...prev, [section]: true }));
      setError(null);
      
      // Only send the relevant data for this section
      let updateData = {};
      switch (section) {
        case 'personal':
          updateData = {
            firstName: profileData.firstName,
            lastName: profileData.lastName
          };
          break;
        case 'jobSearch':
          updateData = {
            employmentStatus: profileData.employmentStatus,
            jobSearchStatus: profileData.jobSearchStatus
          };
          break;
        case 'links':
          updateData = {
            linkedinUrl: profileData.linkedinUrl,
            githubUrl: profileData.githubUrl,
            personalWebsiteUrl: profileData.personalWebsiteUrl
          };
          break;
        default:
          updateData = profileData;
      }
      
      await userService.updateProfile(updateData);
      
      setSuccess(section);
      setEditingSections(prev => ({ ...prev, [section]: false }));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, [section]: false }));
    }
  };

  // Handle section cancel
  const handleSectionCancel = (section) => {
    // Reset to original user data for this section
    if (user) {
      switch (section) {
        case 'personal':
          setProfileData(prev => ({
            ...prev,
            firstName: user.firstName || '',
            lastName: user.lastName || ''
          }));
          break;
        case 'jobSearch':
          setProfileData(prev => ({
            ...prev,
            employmentStatus: user.employmentStatus || '',
            jobSearchStatus: user.jobSearchStatus || ''
          }));
          break;
        case 'skills':
          setProfileData(prev => ({
            ...prev,
            interestedRoleKeywords: user.interestedRoleKeywords || [],
            currentSkills: user.currentSkills || [],
            softwareFamiliarity: user.softwareFamiliarity || []
          }));
          break;
        case 'links':
          setProfileData(prev => ({
            ...prev,
            linkedinUrl: user.linkedinUrl || '',
            githubUrl: user.githubUrl || '',
            personalWebsiteUrl: user.personalWebsiteUrl || ''
          }));
          break;
      }
    }
    setEditingSections(prev => ({ ...prev, [section]: false }));
    setError(null);
  };

  // Handle section edit toggle
  const handleSectionEdit = (section) => {
    setEditingSections(prev => ({ ...prev, [section]: true }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add skill
  const handleAddSkill = async () => {
    if (!newSkill.trim()) return;
    
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.addSkill(newSkill.trim());
      setProfileData(prev => ({
        ...prev,
        currentSkills: [...prev.currentSkills, newSkill.trim()]
      }));
      setNewSkill('');
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error adding skill:', err);
      setError('Failed to add skill.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  // Remove skill
  const handleRemoveSkill = async (skill) => {
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.removeSkill(skill);
      setProfileData(prev => ({
        ...prev,
        currentSkills: prev.currentSkills.filter(s => s !== skill)
      }));
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error removing skill:', err);
      setError('Failed to remove skill.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  // Add software
  const handleAddSoftware = async () => {
    if (!newSoftware.trim()) return;
    
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.addSoftware(newSoftware.trim());
      setProfileData(prev => ({
        ...prev,
        softwareFamiliarity: [...prev.softwareFamiliarity, newSoftware.trim()]
      }));
      setNewSoftware('');
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error adding software:', err);
      setError('Failed to add software.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  // Remove software
  const handleRemoveSoftware = async (software) => {
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.removeSoftware(software);
      setProfileData(prev => ({
        ...prev,
        softwareFamiliarity: prev.softwareFamiliarity.filter(s => s !== software)
      }));
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error removing software:', err);
      setError('Failed to remove software.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  // Add role keyword
  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;
    
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.addRoleKeyword(newKeyword.trim());
      setProfileData(prev => ({
        ...prev,
        interestedRoleKeywords: [...prev.interestedRoleKeywords, newKeyword.trim()]
      }));
      setNewKeyword('');
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error adding keyword:', err);
      setError('Failed to add keyword.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  // Remove role keyword
  const handleRemoveKeyword = async (keyword) => {
    try {
      setLoading(prev => ({ ...prev, skills: true }));
      await userService.removeRoleKeyword(keyword);
      setProfileData(prev => ({
        ...prev,
        interestedRoleKeywords: prev.interestedRoleKeywords.filter(k => k !== keyword)
      }));
      setSuccess('skills');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Error removing keyword:', err);
      setError('Failed to remove keyword.');
    } finally {
      setLoading(prev => ({ ...prev, skills: false }));
    }
  };

  return (
    <>
      <SideNav onLockChange={setSideNavLocked} />
      <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          {/* Main Content */}
          <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
            {/* Header */}
            <div className="mb-8 flex items-center gap-4">
              {/* Profile Picture */}
              <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white font-semibold text-lg">
                {user?.profilePicture ? (
                  <img 
                    src={user.profilePicture} 
                    alt="Profile" 
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  getUserInitial()
                )}
              </div>
              
              {/* Profile Heading */}
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {getUserFirstName()}'s Profile
                </h1>
              </div>
            </div>

            {/* Error Messages */}
            {error && (
              <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg text-red-200">
                {error}
              </div>
            )}

            {/* Profile Information Card */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl group">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Personal Information</h2>
                    
                    {/* Section Edit Button */}
                    <div className="flex gap-2">
                      {editingSections.personal ? (
                        <>
                          <button
                            onClick={() => handleSectionCancel('personal')}
                            disabled={loading.personal}
                            className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 disabled:opacity-50"
                            title="Cancel edit"
                          >
                            <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleSectionSave('personal')}
                            disabled={loading.personal}
                            className="p-1.5 rounded hover:bg-green-500 hover:bg-opacity-20 transition-all duration-200 disabled:opacity-50"
                            title="Save changes"
                          >
                            {loading.personal ? (
                              <svg className="animate-spin w-4 h-4 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-green-400 hover:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleSectionEdit('personal')}
                          className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 opacity-0 group-hover:opacity-100"
                          title="Edit section"
                        >
                          <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Success Message for Personal Section */}
                  {success === 'personal' && (
                    <div className="mb-4 p-3 bg-green-900 bg-opacity-50 border border-green-500 rounded-lg text-green-200 text-sm">
                      Personal information updated successfully!
                    </div>
                  )}

                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                    {editingSections.personal ? (
                      <input
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                        placeholder="Enter first name"
                      />
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.firstName || 'Not provided'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                    {editingSections.personal ? (
                      <input
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                        placeholder="Enter last name"
                      />
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.lastName || 'Not provided'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="text-white">
                        {user?.email || 'Not provided'}
                      </div>
                      {editingSections.personal && (
                        <div className="text-xs text-gray-400 mt-1">
                          Email cannot be changed here
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Job Search Status Section */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl mt-6 group">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Job Search Status</h2>
                    
                    {/* Section Edit Button */}
                    <div className="flex gap-2">
                      {editingSections.jobSearch ? (
                        <>
                          <button
                            onClick={() => handleSectionCancel('jobSearch')}
                            disabled={loading.jobSearch}
                            className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 disabled:opacity-50"
                            title="Cancel edit"
                          >
                            <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleSectionSave('jobSearch')}
                            disabled={loading.jobSearch}
                            className="p-1.5 rounded hover:bg-green-500 hover:bg-opacity-20 transition-all duration-200 disabled:opacity-50"
                            title="Save changes"
                          >
                            {loading.jobSearch ? (
                              <svg className="animate-spin w-4 h-4 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-green-400 hover:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleSectionEdit('jobSearch')}
                          className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 opacity-0 group-hover:opacity-100"
                          title="Edit section"
                        >
                          <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Success Message for Job Search Section */}
                  {success === 'jobSearch' && (
                    <div className="mb-4 p-3 bg-green-900 bg-opacity-50 border border-green-500 rounded-lg text-green-200 text-sm">
                      Job search status updated successfully!
                    </div>
                  )}

                  {/* Employment Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Employment Status</label>
                    {editingSections.jobSearch ? (
                      <select
                        value={profileData.employmentStatus}
                        onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                      >
                        <option value="" className="bg-gray-800">Select employment status</option>
                        <option value="employed" className="bg-gray-800">Employed</option>
                        <option value="unemployed" className="bg-gray-800">Unemployed</option>
                        <option value="contracting" className="bg-gray-800">Contracting</option>
                        <option value="student" className="bg-gray-800">Student</option>
                      </select>
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.employmentStatus ? (
                            profileData.employmentStatus.charAt(0).toUpperCase() + profileData.employmentStatus.slice(1)
                          ) : (
                            'Not specified'
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Job Search Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Job Search Status</label>
                    {editingSections.jobSearch ? (
                      <select
                        value={profileData.jobSearchStatus}
                        onChange={(e) => handleInputChange('jobSearchStatus', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                      >
                        <option value="" className="bg-gray-800">Select job search status</option>
                        <option value="actively_looking" className="bg-gray-800">Actively Looking</option>
                        <option value="passively_looking" className="bg-gray-800">Passively Looking</option>
                        <option value="not_looking" className="bg-gray-800">Not Looking</option>
                        <option value="exploring" className="bg-gray-800">Exploring</option>
                      </select>
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.jobSearchStatus ? (
                            profileData.jobSearchStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                          ) : (
                            'Not specified'
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Skills & Experience Section */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl mt-6 group">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Skills & Experience</h2>
                    
                    {/* Loading indicator for skills operations */}
                    {loading.skills && (
                      <div className="flex items-center gap-2 text-blue-400 text-sm">
                        <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </div>
                    )}
                  </div>

                  {/* Success Message for Skills Section */}
                  {success === 'skills' && (
                    <div className="mb-4 p-3 bg-green-900 bg-opacity-50 border border-green-500 rounded-lg text-green-200 text-sm">
                      Skills updated successfully!
                    </div>
                  )}

                  {/* Role Keywords */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Role Keywords</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="text-white">
                        {profileData.interestedRoleKeywords && profileData.interestedRoleKeywords.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {profileData.interestedRoleKeywords.map((keyword, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-600 text-white"
                              >
                                {keyword}
                                <button
                                  onClick={() => handleRemoveKeyword(keyword)}
                                  className="ml-2 text-blue-200 hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          'No role keywords specified'
                        )}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={newKeyword}
                          onChange={(e) => setNewKeyword(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                          className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500"
                          placeholder="Add role keyword"
                          disabled={loading.skills}
                        />
                        <button
                          onClick={handleAddKeyword}
                          disabled={loading.skills}
                          className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Current Skills */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Current Skills</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="text-white">
                        {profileData.currentSkills && profileData.currentSkills.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {profileData.currentSkills.map((skill, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-600 text-white"
                              >
                                {skill}
                                <button
                                  onClick={() => handleRemoveSkill(skill)}
                                  className="ml-2 text-green-200 hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          'No skills specified'
                        )}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                          className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500"
                          placeholder="Add skill"
                          disabled={loading.skills}
                        />
                        <button
                          onClick={handleAddSkill}
                          disabled={loading.skills}
                          className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Software Familiarity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Software Familiarity</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="text-white">
                        {profileData.softwareFamiliarity && profileData.softwareFamiliarity.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {profileData.softwareFamiliarity.map((software, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-600 text-white"
                              >
                                {software}
                                <button
                                  onClick={() => handleRemoveSoftware(software)}
                                  className="ml-2 text-purple-200 hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          'No software specified'
                        )}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={newSoftware}
                          onChange={(e) => setNewSoftware(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddSoftware()}
                          className="flex-1 bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500"
                          placeholder="Add software"
                          disabled={loading.skills}
                        />
                        <button
                          onClick={handleAddSoftware}
                          disabled={loading.skills}
                          className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Links Section */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl mt-6 group">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Professional Links</h2>
                    
                    {/* Section Edit Button */}
                    <div className="flex gap-2">
                      {editingSections.links ? (
                        <>
                          <button
                            onClick={() => handleSectionCancel('links')}
                            disabled={loading.links}
                            className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 disabled:opacity-50"
                            title="Cancel edit"
                          >
                            <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleSectionSave('links')}
                            disabled={loading.links}
                            className="p-1.5 rounded hover:bg-green-500 hover:bg-opacity-20 transition-all duration-200 disabled:opacity-50"
                            title="Save changes"
                          >
                            {loading.links ? (
                              <svg className="animate-spin w-4 h-4 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-green-400 hover:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleSectionEdit('links')}
                          className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200 opacity-0 group-hover:opacity-100"
                          title="Edit section"
                        >
                          <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Success Message for Links Section */}
                  {success === 'links' && (
                    <div className="mb-4 p-3 bg-green-900 bg-opacity-50 border border-green-500 rounded-lg text-green-200 text-sm">
                      Professional links updated successfully!
                    </div>
                  )}

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">LinkedIn Profile</label>
                    {editingSections.links ? (
                      <input
                        type="url"
                        value={profileData.linkedinUrl}
                        onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                        placeholder="https://linkedin.com/in/yourprofile"
                      />
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.linkedinUrl ? (
                            <a 
                              href={profileData.linkedinUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline"
                            >
                              {profileData.linkedinUrl}
                            </a>
                          ) : (
                            'Not provided'
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* GitHub */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">GitHub Profile</label>
                    {editingSections.links ? (
                      <input
                        type="url"
                        value={profileData.githubUrl}
                        onChange={(e) => handleInputChange('githubUrl', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                        placeholder="https://github.com/yourusername"
                      />
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.githubUrl ? (
                            <a 
                              href={profileData.githubUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline"
                            >
                              {profileData.githubUrl}
                            </a>
                          ) : (
                            'Not provided'
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Personal Website */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Personal Website</label>
                    {editingSections.links ? (
                      <input
                        type="url"
                        value={profileData.personalWebsiteUrl}
                        onChange={(e) => handleInputChange('personalWebsiteUrl', e.target.value)}
                        className="w-full bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200"
                        placeholder="https://yourwebsite.com"
                      />
                    ) : (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {profileData.personalWebsiteUrl ? (
                            <a 
                              href={profileData.personalWebsiteUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline"
                            >
                              {profileData.personalWebsiteUrl}
                            </a>
                          ) : (
                            'Not provided'
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>


          </div>
        </PageLayout>
      </div>
    </>
  );
}

export default Profile; 